import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { socketService, BoxData, ModelUpdateResponse, BoxDataResponse, TrainingProgressData } from '../services/trainingSocketService';
import { DatasetOverview } from '../types/training'; // Import DatasetOverview

export type TrainingStatus = 'idle' | 'started' | 'progress' | 'completed' | 'error';

// Define the Socket Context type
interface SocketContextType {
  isConnected: boolean;
  socket: any | null; // Socket instance for direct access
  processFiles: (files: any) => void;
  processBoxData: (data: BoxData) => void;
  modelUpdateData: ModelUpdateResponse | null;
  boxDataResponse: BoxDataResponse | null;
  startTraining: (modelName?: string) => void;
  cancelTraining: (job_id?: string) => void;
  trainingStatus: TrainingStatus;
  trainingProgressData: TrainingProgressData[];
  lastTrainingError: string | null;
  datasetOverview: DatasetOverview | null;
  modelStructure: any; // NEW: Model structure for visualization
  modelEvaluationInfo: any | null; // Model evaluation information
  requestModelEvaluationInfo: () => void;
  triggerManualReevaluation: () => void;
}

// Create the context with default values
const TrainingSocketContext = createContext<SocketContextType>({
  isConnected: false,
  socket: null,
  processFiles: () => {},
  processBoxData: () => {},
  modelUpdateData: null,
  boxDataResponse: null,
  startTraining: (_modelName?: string) => {},
  cancelTraining: (_job_id?: string) => {},
  trainingStatus: 'idle',
  trainingProgressData: [],
  lastTrainingError: null,
  datasetOverview: null,
  modelStructure: null,
  modelEvaluationInfo: null,
  requestModelEvaluationInfo: () => {},
  triggerManualReevaluation: () => {},
});

// Hook to use the socket context
export const useTrainingSocket = () => useContext(TrainingSocketContext);

interface TrainingSocketProviderProps {
  children: ReactNode;
}

// Socket context provider component
export const TrainingSocketProvider: React.FC<TrainingSocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [modelUpdateData, setModelUpdateData] = useState<ModelUpdateResponse | null>(null);
  const [boxDataResponse, setBoxDataResponse] = useState<BoxDataResponse | null>(null);
  const [trainingStatus, setTrainingStatus] = useState<TrainingStatus>('idle');
  const [trainingProgressData, setTrainingProgressData] = useState<TrainingProgressData[]>([]);
  const [lastTrainingError, setLastTrainingError] = useState<string | null>(null);
  const [datasetOverview, setDatasetOverview] = useState<DatasetOverview | null>(null);
  const [modelStructure, setModelStructure] = useState<any>(null);
  const [modelEvaluationInfo, setModelEvaluationInfo] = useState<any | null>(null);

  // Initialize connection (runs only once on mount)
  useEffect(() => {
    const initializeSocket = async () => {
      try {
        await socketService.connect();
      } catch (error) {
        console.error('Error connecting socket:', error);
      }
    };

    initializeSocket();

    // Set up event listeners
    const connectHandler = () => {
      setIsConnected(true);
      // Request dataset overview after successful connection
      socketService.getDatasetOverview();
    };

    const disconnectHandler = () => {
      setIsConnected(false);
    };

    const modelUpdateHandler = (data: ModelUpdateResponse) => {
      setModelUpdateData(data);
    };

    const boxDataHandler = (data: BoxDataResponse) => {
      setBoxDataResponse(data);
    };

    const errorHandler = (error: any) => {
      // Potentially set a generic error state here if needed
    };

    const trainingStartedHandler = (data: any) => {
      setTrainingStatus('started');
      setTrainingProgressData([]); // Clear previous progress
      setLastTrainingError(null);
    };

    const trainingBatchProgressHandler = (data: TrainingProgressData) => {
      setTrainingStatus('progress');
      setTrainingProgressData((prevData) => [...prevData, data]);
      // Log each epoch with all data
      console.log(`[EPOCH ${data.epoch}] Loss: ${data.train_loss.toFixed(4)} | Val Loss: ${data.val_loss.toFixed(4)}`);
    };

    const trainingCompletedHandler = (data: any) => {
      setTrainingStatus('completed');
    };

    const trainingErrorHandler = (data: { message?: string }) => {
      setTrainingStatus('error');
      setLastTrainingError(data.message || 'Unknown training error');
    };

    const trainingCancelledHandler = (data: any) => {
      setTrainingStatus('idle');
      setLastTrainingError(null);
      console.log('[TRAINING] Training cancelled:', data);
    };

    // Register event listeners
    socketService.on('connect', connectHandler);
    socketService.on('disconnect', disconnectHandler);
    socketService.on('modelUpdate', modelUpdateHandler);
    socketService.on('boxDataResponse', boxDataHandler);
    socketService.on('error', errorHandler);

    socketService.on('trainingStarted', trainingStartedHandler);
    socketService.on('trainingProgress', trainingBatchProgressHandler);
    socketService.on('trainingCompleted', trainingCompletedHandler);
    socketService.on('trainingError', trainingErrorHandler);
    socketService.on('trainingCancelled', trainingCancelledHandler);

    // Handler for dataset overview
    // Note: Backend now sends raw datastructure.json array, not DatasetOverview format
    const datasetOverviewHandler = (data: any) => {
      // Handle both old format (DatasetOverview) and new format (raw datastructure array)
      if (data.status === 'success' && data.data) {
        // New format: data.data is the array of samples
        setDatasetOverview(data.data as any);
      } else if (data.training || data.validation) {
        // Old format: DatasetOverview
        setDatasetOverview(data);
      } else {
        setDatasetOverview(data);
      }
    };

    socketService.on('datasetOverview', datasetOverviewHandler);

    // Handler for model structure from backend
    const modelStructureHandler = (data: any) => {
      console.log('[MODEL STRUCTURE RECEIVED]', `Layers: ${data.layers?.length || 0}`);
      
      // Only update if structure actually changed (deep comparison)
      setModelStructure((prev: any) => {
        if (prev && JSON.stringify(prev) === JSON.stringify(data)) {
          console.log('[MODEL STRUCTURE] Structure unchanged, skipping update');
          return prev; // Return same reference to prevent re-renders
        }
        return data;
      });
    };

    socketService.on('modelStructure', modelStructureHandler);

    // Handler for model evaluation info
    const modelEvaluationInfoHandler = (data: any) => {
      console.log('[MODEL EVALUATION INFO RECEIVED]', data);
      setModelEvaluationInfo(data);
    };

    socketService.on('modelEvaluationInfo', modelEvaluationInfoHandler);

    // Handler for reevaluation triggered
    const reevaluationTriggeredHandler = (data: any) => {
      console.log('[REEVALUATION TRIGGERED]', data);
      // Could update UI to show reevaluation in progress
    };

    socketService.on('reevaluationTriggered', reevaluationTriggeredHandler);

    // Handler for reevaluation complete
    const reevaluationCompleteHandler = (data: any) => {
      console.log('[REEVALUATION COMPLETE]', data);
      // Could refresh evaluation info
    };

    socketService.on('reevaluationComplete', reevaluationCompleteHandler);


    // Cleanup on component unmount
    // NOTE: We do NOT disconnect the socket here - it should persist across route changes
    // The socketService is a singleton and should remain connected for process windows
    return () => {
      socketService.off('connect', connectHandler);
      socketService.off('disconnect', disconnectHandler);
      socketService.off('modelUpdate', modelUpdateHandler);
      socketService.off('boxDataResponse', boxDataHandler);
      socketService.off('error', errorHandler);
      socketService.off('trainingStarted', trainingStartedHandler);
      socketService.off('trainingProgress', trainingBatchProgressHandler);
      socketService.off('trainingCompleted', trainingCompletedHandler);
      socketService.off('trainingError', trainingErrorHandler);
      socketService.off('trainingCancelled', trainingCancelledHandler);
      socketService.off('datasetOverview', datasetOverviewHandler);
      socketService.off('modelStructure', modelStructureHandler);
      socketService.off('modelEvaluationInfo', modelEvaluationInfoHandler);
      socketService.off('reevaluationTriggered', reevaluationTriggeredHandler);
      socketService.off('reevaluationComplete', reevaluationCompleteHandler);
      // DO NOT disconnect here - connection should persist for process windows
      // socketService.disconnect();
    };
  }, []); // Empty dependency array - run only once on mount

  const startTraining = (modelName?: string) => {
    if (isConnected) {
      // Get model name from parameter, or from boxDataResponse
      // CRITICAL: Always get model name from parameter first (passed from handler)
      const finalModelName = modelName || boxDataResponse?.data?.model_name || boxDataResponse?.data?.modelName;
      
      if (!finalModelName) {
        console.error('[START TRAINING] ERROR: No model name provided! This will default to "Untitled Project"');
      }
      
      // Pass the boxDataResponse data (architecture) to the backend
      const trainingData: any = {
        status: 'initiated',
        ...(boxDataResponse?.data || {}), // Include architecture and generatedModelPath from boxDataResponse
      };
      
      // CRITICAL: Always explicitly set modelName and model_name
      // Backend checks modelName first, then model_name
      // Even if finalModelName is undefined, set it so backend knows it's missing
      trainingData.modelName = finalModelName;
      trainingData.model_name = finalModelName; // Also set snake_case for compatibility
      
      console.log('[START TRAINING] Sending training data:', {
        modelName: trainingData.modelName,
        model_name: trainingData.model_name,
        hasArchitecture: !!trainingData.architecture,
        finalModelName,
        allKeys: Object.keys(trainingData),
        boxDataResponseKeys: boxDataResponse?.data ? Object.keys(boxDataResponse.data) : 'no boxDataResponse'
      });
      socketService.emit('startTraining', trainingData);
      // Backend now sends 'trainingStarted' event, so we set status there
    }
  };

  const cancelTraining = (job_id?: string) => {
    if (isConnected) {
      socketService.cancelTraining(job_id);
    }
  };

  const requestModelEvaluationInfo = () => {
    if (isConnected) {
      socketService.requestModelEvaluationInfo();
    }
  };

  const triggerManualReevaluation = () => {
    if (isConnected) {
      socketService.triggerManualReevaluation();
    }
  };

  // Value to be provided to consumers
  const value: SocketContextType = {
    isConnected,
    socket: socketService.getSocket(), // Expose socket instance
    processFiles: (files) => socketService.processFiles(files),
    processBoxData: (data) => socketService.processBoxData(data),
    modelUpdateData,
    boxDataResponse,
    startTraining,
    cancelTraining,
    trainingStatus,
    trainingProgressData,
    lastTrainingError,
    datasetOverview, // Provide dataset overview
    modelStructure, // Provide model structure for visualization
    modelEvaluationInfo, // Provide model evaluation info
    requestModelEvaluationInfo, // Method to request evaluation info
    triggerManualReevaluation, // Method to trigger manual reevaluation
  };

  return <TrainingSocketContext.Provider value={value}>{children}</TrainingSocketContext.Provider>;
};

export default TrainingSocketContext;

